import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { MdArticle, MdPeople, MdCampaign, MdVisibility, MdTrendingUp, MdKeyboardArrowDown, MdCalendarMonth, MdAutoAwesome, MdComment, MdFactCheck, MdFlag, MdArrowForward, MdPublic } from 'react-icons/md';
import { useAuth } from '../context/AuthContext';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

const TELUGU_TO_ENGLISH_REGION = {
  'అమరావతి': 'Amaravati',
  'అమలాపురం': 'Amalapuram',
  'అల్ ఇండియా': 'All India',
  'ఆల్ ఇండియా': 'All India',
  'ఆల్_ఇండియా': 'All India',
  'ఆదిలాబాద్': 'Adilabad',
  'ఏలూరు': 'Eluru',
  'ఒంగోలు': 'Ongole',
  'కడప': 'Kadapa',
  'వైఎస్సార్ కడప': 'Kadapa',
  'కరీంనగర్': 'Karimnagar',
  'కర్నూలు': 'Kurnool',
  'కాకినాడ': 'Kakinada',
  'కామరెడ్డి': 'Kamareddy',
  'కోనసీమ': 'Konaseema',
  'ఖమ్మం': 'Khammam',
  'గుంటూరు': 'Guntur',
  'చిత్తూరు': 'Chittoor',
  'తిరుపతి': 'Tirupati',
  'నంద్యాల': 'Nandyal',
  'నాగర్ కర్నూల్': 'Nagarkurnool',
  'నాగర్‌కర్నూల్': 'Nagarkurnool',
  'నారాయణపేట': 'Narayanpet',
  'నిజామాబాద్': 'Nizamabad',
  'నిర్మల్': 'Nirmal',
  'నెల్లూరు': 'Nellore',
  'పశ్చిమగోదావరి జిల్లా': 'West Godavari',
  'పశ్చిమ గోదావరి': 'West Godavari',
  'ప్రకాశం': 'Prakasam',
  'బాపట్ల': 'Bapatla',
  'భద్రాద్రి కొత్తగూడెం': 'Bhadradri Kothagudem',
  'మంచిర్యాల': 'Mancherial',
  'మార్కాపురం': 'Markapur',
  'మెదక్': 'Medak',
  'మేడ్చల్ మల్కాజిగిరి': 'Medchal-Malkajgiri',
  'మేడ్చల్ మల్కాజ్‌గిరి': 'Medchal-Malkajgiri',
  'రంగారెడ్డి': 'Ranga Reddy',
  'రాజమండ్రి': 'Rajahmundry',
  'వనపర్తి': 'Wanaparthy',
  'వరంగల్': 'Warangal',
  'వికారాబాద్': 'Vikarabad',
  'విజయవాడ': 'Vijayawada',
  'విశాఖపట్నం': 'Visakhapatnam',
  'వేములవాడ': 'Vemulawada',
  'శ్రీకాకుళం': 'Srikakulam',
  'సంగారెడ్డి': 'Sangareddy',
  'సిద్దిపేట': 'Siddipet',
  'సిద్ధిపేట': 'Siddipet',
  'సూర్యాపేట': 'Suryapet',
  'హైదరాబాద్': 'Hyderabad',
  'రాజన్న సిరిసిల్ల': 'Rajanna Sircilla',
  'సిరిసిల్ల': 'Rajanna Sircilla',
  'మన్యం': 'Manyam',
  'తెలంగాణ': 'Telangana',
  'ఆంధ్రప్రదేశ్': 'Andhra Pradesh',
  'all_india': 'All India',
  'nirmal': 'Nirmal',
};

function formatRegionToEnglish(name) {
  if (!name) return 'All India';
  const trimmed = String(name).trim();
  if (TELUGU_TO_ENGLISH_REGION[trimmed]) return TELUGU_TO_ENGLISH_REGION[trimmed];
  const lower = trimmed.toLowerCase();
  if (TELUGU_TO_ENGLISH_REGION[lower]) return TELUGU_TO_ENGLISH_REGION[lower];
  return trimmed
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

// ChartWrapper: measures its own DOM width via ResizeObserver,
// then passes it directly to the Recharts chart as a fixed pixel width.
// This bypasses ResponsiveContainer's CSS Grid/Flex 0px measurement bug.
function ChartWrapper({ height, children }) {
  const wrapperRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    // Initial measurement
    setWidth(el.getBoundingClientRect().width);
    // Watch for resizes
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setWidth(w);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%', height: height, overflow: 'hidden' }}>
      {width > 0 && children(width, height)}
    </div>
  );
}

export default function Dashboard() {
  const { admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today');
  const [prevFilter, setPrevFilter] = useState('today');
  const [skipFetch, setSkipFetch] = useState(false);

  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [error, setError] = useState('');
  const [showCustomPanel, setShowCustomPanel] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [articleTab, setArticleTab] = useState('today');

  const articleTabs = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7', label: 'Last 7 Days' },
    { key: 'last30', label: 'Last 30 Days' },
  ];
  const dropdownRef = useRef(null);

  // Chart data states
  const [readsData, setReadsData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [topArticles, setTopArticles] = useState({ today: [], yesterday: [], last7: [], last30: [] });
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);

  // Reporters leaderboard state
  const [reportersLeaderboard, setReportersLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(true);

  // Posts per category state
  const [categoriesPosts, setCategoriesPosts] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filterOptions = [
    { value: 'total', label: 'Total / All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7', label: 'Last 7 Days' },
    { value: 'last15', label: 'Last 15 Days' },
    { value: 'last30', label: 'Last 30 Days' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const selectedLabel = filterOptions.find(o => o.value === filter)?.label || 'Today';

  // Unified fetch for all dashboard data and analytics charts
  const fetchDashboardData = useCallback((selectedFilter, fromDate, toDate) => {
    setLoading(true);
    setChartsLoading(true);
    setLeaderboardLoading(true);
    setCategoriesLoading(true);
    setError('');

    let queryParams = `range=${selectedFilter}`;
    if (selectedFilter === 'custom') {
      if (!fromDate || !toDate) {
        setError('Both From Date and To Date are required.');
        setLoading(false);
        setChartsLoading(false);
        setLeaderboardLoading(false);
        setCategoriesLoading(false);
        return;
      }
      queryParams += `&from_date=${fromDate}&to_date=${toDate}`;
    }

    Promise.all([
      api.get(`/admin/analytics/overview?${queryParams}`),
      api.get(`/admin/analytics/reads-trend?${queryParams}`),
      api.get(`/admin/analytics/users-by-region?${queryParams}&limit=10`),
      api.get(`/admin/analytics/users-vs-guests?${queryParams}`),
      api.get(`/admin/analytics/posts-per-category?${queryParams}`),
      api.get(`/admin/analytics/reporters-leaderboard?${queryParams}`),
      api.get(`/admin/analytics/most-viewed-articles?${queryParams}`),
      api.get(`/admin/analytics/most-viewed-articles?range=today`),
      api.get(`/admin/analytics/most-viewed-articles?range=yesterday`),
      api.get(`/admin/analytics/most-viewed-articles?range=last7`),
      api.get(`/admin/analytics/most-viewed-articles?range=last30`),
    ]).then(([overviewRes, readsRes, regionsRes, usersVsGuestsRes, categoriesRes, leaderboardRes, articlesRes, todayArticles, yesterdayArticles, last7Articles, last30Articles]) => {
      // Overview KPIs
      setStats(overviewRes.data.data);

      // Reads trend
      const processedReads = (readsRes.data.data || []).map(item => {
        if (!item || !item.day) return null;
        return {
          day: item.day,
          reads: parseInt(item.reads, 10) || 0
        };
      }).filter(Boolean);
      setReadsData(processedReads);

      // Users by region
      const processedRegions = (regionsRes.data.data || []).map(item => ({
        region: formatRegionToEnglish(item.city) || 'Unknown',
        users: item.users || 0
      }));
      setRegionData(processedRegions);

      // Users vs guests
      setUserGrowthData(usersVsGuestsRes.data.data || []);

      // Posts per Category
      const sortedCats = (categoriesRes.data.data || []).map(cat => ({
        name: cat.category || 'Uncategorized',
        total: cat.total || 0,
        daysList: cat.daily || []
      }));
      setCategoriesPosts(sortedCats);

      // Reporters Leaderboard
      const leaderboard = (leaderboardRes.data.data || []).map(item => ({
        id: item.rank,
        name: item.reporter || 'Unknown',
        posted: item.posted || 0
      }));
      setReportersLeaderboard(leaderboard);

      // Top Articles Tabs
      const mapArticles = (res) => (res.data.data || []).map(item => ({
        title: item.title || 'Untitled',
        views: item.views || 0
      }));
      setTopArticles({
        today: mapArticles(todayArticles),
        yesterday: mapArticles(yesterdayArticles),
        last7: mapArticles(last7Articles),
        last30: mapArticles(last30Articles),
      });
    }).catch(err => {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    }).finally(() => {
      setLoading(false);
      setChartsLoading(false);
      setLeaderboardLoading(false);
      setCategoriesLoading(false);
    });
  }, []);

  useEffect(() => {
    if (skipFetch) {
      setSkipFetch(false);
      return;
    }
    if (filter !== 'custom') {
      setCustomFromDate('');
      setCustomToDate('');
      setError('');
      setShowCustomPanel(false);
      fetchDashboardData(filter);

      const intervalId = setInterval(() => {
        api.get(`/admin/analytics/overview?range=${filter}`)
          .then(res => setStats(res.data.data))
          .catch(() => {});
      }, 30000);

      return () => clearInterval(intervalId);
    } else {
      setShowCustomPanel(true);
    }
  }, [filter, fetchDashboardData]);

  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    if (newFilter === 'custom') {
      setPrevFilter(filter);
    }
    setFilter(newFilter);
  };

  const handleApplyCustom = () => {
    if (!customFromDate || !customToDate) {
      setError('Both From Date and To Date are required.');
      return;
    }
    const from = new Date(customFromDate);
    const to = new Date(customToDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (from > to) {
      setError('From Date cannot be greater than To Date.');
      return;
    }
    if (from > today || to > today) {
      setError('Future dates are not allowed.');
      return;
    }

    fetchDashboardData('custom', customFromDate, customToDate);
    setShowCustomPanel(false);
  };

  const handleCancelCustom = () => {
    setShowCustomPanel(false);
    setError('');
    setSkipFetch(true);
    setFilter(prevFilter);
  };

  const cards = [
    { label: 'Articles', value: stats?.articles ?? stats?.articlesToday ?? 0, icon: MdArticle, color: '#DC2626' },
    { label: 'Users', value: stats?.users ?? stats?.totalUsers ?? 0, icon: MdPeople, color: '#10B981' },
    { label: 'Reads', value: stats?.reads ?? stats?.readsToday ?? 0, icon: MdVisibility, color: '#6366F1' },
    { label: 'Guest Devices', value: stats?.guest_devices ?? stats?.totalGuests ?? 0, icon: MdTrendingUp, color: '#8B5CF6' },
    { label: 'Online Now', value: stats?.online_now ?? stats?.onlineNow ?? 0, icon: MdPublic, color: '#F59E0B' },
  ];

  return (
    <div className="page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showCustomPanel ? '10px' : '20px', flexWrap: 'wrap', gap: '24px' }}>
        <div style={{ fontFamily: 'Ramabhadra, serif', fontSize: '28px', color: 'rgb(29 62 109)', fontWeight: '400', lineHeight: '1.5', flex: '1', minWidth: '0' }}>
          Trusted news, delivered every morning
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div className="date-filter" ref={dropdownRef} style={{ position: 'relative', width: 'fit-content' }}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '10px 16px', height: '44px',
                borderRadius: '12px', border: '1px solid #F0F4F8',
                background: '#fff', color: '#0F172A',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                boxShadow: isDropdownOpen ? '0 4px 12px rgba(15, 23, 42, 0.08)' : '0 2px 6px rgba(15, 23, 42, 0.04)',
                transition: 'all 0.2s ease',
                outline: 'none',
                letterSpacing: '0.3px',
              }}
              onMouseOver={(e) => { if (!isDropdownOpen) { e.currentTarget.style.background = '#FAFBFC'; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.06)'; } }}
              onMouseOut={(e) => { if (!isDropdownOpen) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#F0F4F8'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(15, 23, 42, 0.04)'; } }}
            >
              <MdCalendarMonth style={{ fontSize: '18px', color: '#EF233C' }} />
              <span style={{ fontWeight: '600' }}>{selectedLabel}</span>
              <MdKeyboardArrowDown style={{ fontSize: '18px', marginLeft: '4px', transform: isDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease', color: '#64748B' }} />
            </button>

            {isDropdownOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
                background: '#fff', borderRadius: '8px', border: '1px solid #E2E8F0',
                boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                zIndex: 50, padding: '8px', minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '4px'
              }}>
                {filterOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      handleFilterChange({ target: { value: opt.value } });
                      setIsDropdownOpen(false);
                    }}
                    style={{
                      padding: '8px 12px', textAlign: 'left', borderRadius: '6px',
                      background: filter === opt.value ? 'rgba(239, 35, 60, 0.08)' : 'transparent',
                      color: filter === opt.value ? '#EF233C' : '#374151',
                      fontSize: '14px', fontWeight: filter === opt.value ? '600' : '400',
                      cursor: 'pointer', border: 'none', width: '100%',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => { if (filter !== opt.value) e.currentTarget.style.background = '#F1F5F9'; }}
                    onMouseOut={(e) => { if (filter !== opt.value) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showCustomPanel && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <input
            type={customFromDate ? 'date' : 'text'}
            placeholder="Start Date 📅"
            onFocus={(e) => (e.target.type = 'date')}
            onBlur={(e) => (!e.target.value && (e.target.type = 'text'))}
            value={customFromDate}
            onChange={(e) => setCustomFromDate(e.target.value)}
            style={{
              height: '42px',
              padding: '0 16px',
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
              outline: 'none',
              width: '160px',
              fontSize: '14px',
              color: '#0F172A',
              background: '#fff',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
            }}
          />
          <input
            type={customToDate ? 'date' : 'text'}
            placeholder="End Date 📅"
            onFocus={(e) => (e.target.type = 'date')}
            onBlur={(e) => (!e.target.value && (e.target.type = 'text'))}
            value={customToDate}
            onChange={(e) => setCustomToDate(e.target.value)}
            style={{
              height: '42px',
              padding: '0 16px',
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
              outline: 'none',
              width: '160px',
              fontSize: '14px',
              color: '#0F172A',
              background: '#fff',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
            }}
          />
          <button
            onClick={handleApplyCustom}
            className="btn btn-primary"
            style={{
              height: '42px',
              padding: '0 24px',
              margin: 0,
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '8px',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
            }}
          >
            Apply
          </button>
          <button
            onClick={handleCancelCustom}
            style={{
              height: '42px',
              padding: '0 24px',
              margin: 0,
              background: '#fff',
              color: '#64748B',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#F8FAFC'}
            onMouseOut={(e) => e.currentTarget.style.background = '#fff'}
          >
            Cancel
          </button>
          {error && <span style={{ color: 'red', fontSize: '14px', marginLeft: '10px', fontWeight: '500' }}>{error}</span>}
        </div>
      )}

      {!showCustomPanel && error && <div style={{ color: 'red', marginBottom: '15px', fontWeight: 'bold' }}>{error}</div>}
      {loading && stats ? <div style={{ opacity: 0.5 }}>Updating...</div> : null}

      <div className="stats-grid">
        {cards.map((card, i) => (
          <div key={i} className="stat-card">
            <div className="stat-icon" style={{ color: card.color, fontSize: '24px' }}>
              <card.icon />
            </div>
            <div className="stat-info">
              <h3 style={{ color: card.color }}>{card.value}</h3>
              <p>{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* 70-30 Layout: Charts and Leaderboard */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* 70% Column - Charts */}
        <div style={{ flex: '0 0 68%', minWidth: 0, overflow: 'hidden' }}>
          {/* Reads Chart - Full Width */}
          <div className="dashboard-card" style={{ minWidth: 0 }}>
            <h3 className="chart-title">Reads Analytics - {selectedLabel}</h3>
            {chartsLoading ? (
              <div style={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
                Loading chart data...
              </div>
            ) : readsData.length === 0 ? (
              <div style={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
                No analytics data available.
              </div>
            ) : (
              <ChartWrapper height={350}>
                {(chartWidth, chartHeight) => (
                  <AreaChart width={chartWidth} height={chartHeight} data={readsData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorReads" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#D71920" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#D71920" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F4F8" vertical={false} />
                    <XAxis dataKey="day" stroke="#64748B" style={{ fontSize: '13px' }} />
                    <YAxis stroke="#64748B" style={{ fontSize: '13px' }} />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                      cursor={{ stroke: '#D71920', strokeWidth: 2 }}
                    />
                    <Area type="monotone" dataKey="reads" stroke="#D71920" strokeWidth={2.5} fillOpacity={1} fill="url(#colorReads)" activeDot={{ r: 6 }} />
                  </AreaChart>
                )}
              </ChartWrapper>
            )}
          </div>

          {/* User Growth Chart - Users vs Guests */}
          <div className="dashboard-card" style={{ minWidth: 0 }}>
            <h3 className="chart-title">Users vs Guests Trend - {selectedLabel}</h3>
            {chartsLoading ? (
              <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
                Loading chart data...
              </div>
            ) : userGrowthData.length === 0 ? (
              <div style={{ height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
                No analytics data available.
              </div>
            ) : (
              <ChartWrapper height={300}>
                {(chartWidth, chartHeight) => (
                  <LineChart width={chartWidth} height={chartHeight} data={userGrowthData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F0F4F8" vertical={false} />
                    <XAxis dataKey="month" stroke="#64748B" style={{ fontSize: '13px' }} />
                    <YAxis stroke="#64748B" style={{ fontSize: '13px' }} />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                      cursor={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                    <Line type="monotone" dataKey="users" stroke="#10B981" strokeWidth={3} dot={{ fill: '#10B981', r: 4 }} activeDot={{ r: 6 }} name="Users" />
                    <Line type="monotone" dataKey="guests" stroke="#8B5CF6" strokeWidth={3} dot={{ fill: '#8B5CF6', r: 4 }} name="Guests" />
                  </LineChart>
                )}
              </ChartWrapper>
            )}
          </div>

          {/* Two Column Layout: Articles & Region Progress Bars */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            {/* Top Articles with Tabs */}
            <div className="dashboard-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 className="chart-title" style={{ margin: 0 }}>Most Viewed Articles</h3>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {articleTabs.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setArticleTab(tab.key)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: articleTab === tab.key ? '2px solid #EF233C' : '1px solid #E2E8F0',
                        background: articleTab === tab.key ? '#FFF1F2' : '#fff',
                        color: articleTab === tab.key ? '#EF233C' : '#64748B',
                        fontSize: '14px',
                        fontWeight: articleTab === tab.key ? '600' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #F0F4F8' }}>
                      <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', color: '#64748B', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rank</th>
                      <th style={{ textAlign: 'left', padding: '10px', fontWeight: '600', color: '#64748B', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Title</th>
                      <th style={{ textAlign: 'right', padding: '10px', fontWeight: '600', color: '#64748B', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topArticles[articleTab].map((article, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #F0F4F8', transition: 'background 0.2s ease' }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#FAFBFC'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '10px', color: '#0F172A', fontWeight: '500', fontSize: '17px', fontFamily: 'Ramabhadra, serif' }}>{idx + 1}</td>
                        <td style={{ padding: '10px', color: '#0F172A', fontSize: '17px', fontWeight: '400', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Ramabhadra, serif' }}>{article.title}</td>
                        <td style={{ padding: '10px', textAlign: 'right', color: '#DC2626', fontWeight: '500', fontSize: '17px', fontFamily: 'Ramabhadra, serif' }}>{(article.views || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Users by Region - Progress Bars */}
            <div className="dashboard-card">
              <h3 className="chart-title" style={{ marginBottom: '20px' }}>Users by Region - Top 10 ({selectedLabel})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {regionData.slice(0, 10).map((item, idx) => {
                  const maxUsers = Math.max(...regionData.slice(0, 10).map(r => r.users));
                  const percentage = (item.users / maxUsers) * 100;
                  return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '15px', fontWeight: '500', color: '#0F172A' }}>{idx + 1}. {item.region}</span>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748B' }}>{item.users.toLocaleString()}</span>
                      </div>
                      <div style={{
                        height: '8px',
                        background: '#F0F4F8',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${percentage}%`,
                          background: `linear-gradient(90deg, #8B5CF6, #6366F1)`,
                          borderRadius: '4px',
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* 30% Column - Quick Links & Leaderboard */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Quick Links */}
          <div className="dashboard-card">
            <h3 className="chart-title" style={{ marginBottom: '16px' }}>Quick Links</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { icon: MdAutoAwesome, label: 'Status Maker', path: '/status', color: '#6366F1' },
                { icon: MdComment, label: 'Comments', path: '/comments', color: '#EC4899' },
                { icon: MdFactCheck, label: 'Contributor News', path: '/contributor-review', color: '#F59E0B' },
                { icon: MdFlag, label: 'Moderation', path: '/moderation', color: '#EF4444' },
              ].map((link, idx) => (
                <Link key={idx} to={link.path} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px 12px',
                  borderRadius: '12px',
                  background: `${link.color}12`,
                  border: `1.5px solid ${link.color}24`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none',
                  color: 'inherit',
                }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = `${link.color}20`;
                    e.currentTarget.style.borderColor = `${link.color}40`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = `${link.color}12`;
                    e.currentTarget.style.borderColor = `${link.color}24`;
                  }}
                >
                  <link.icon style={{ fontSize: '24px', color: link.color }} />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A', textAlign: 'center', lineHeight: 1.2 }}>
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Reporters Leaderboard */}
          <div className="dashboard-card">
            <h3 className="chart-title" style={{ marginBottom: '20px' }}>Reporters Leaderboard - {selectedLabel}</h3>
            {leaderboardLoading ? (
              <div style={{ textAlign: 'center', color: '#64748B', padding: '20px' }}>
                Loading leaderboard...
              </div>
            ) : reportersLeaderboard.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748B', padding: '20px' }}>
                No reporters yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {reportersLeaderboard.map((reporter, idx) => (
                  <div key={reporter.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    background: idx === 0 ? '#FFF1F2' : idx === 1 ? '#F0F9FF' : idx === 2 ? '#FFFBEB' : 'transparent',
                    borderRadius: '10px',
                    borderLeft: `3px solid ${['#EF233C', '#3B82F6', '#F59E0B'][idx] || '#E2E8F0'}`,
                    transition: 'all 0.2s ease',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: ['#EF233C', '#3B82F6', '#F59E0B'][idx] || '#E2E8F0',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '16px',
                        flexShrink: 0
                      }}>
                        {idx + 1}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {reporter.name}
                        </p>
                        <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#64748B', fontWeight: '500' }}>
                          Posted
                        </p>
                      </div>
                    </div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: ['#EF233C', '#3B82F6', '#F59E0B'][idx] || '#6366F1',
                      minWidth: 'fit-content',
                      marginLeft: '8px'
                    }}>
                      {reporter.posted}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Posts Per Category */}
          <div className="dashboard-card">
            <h3 className="chart-title" style={{ marginBottom: '16px' }}>Posts Per Category - {selectedLabel}</h3>
            {categoriesLoading ? (
              <div style={{ textAlign: 'center', color: '#64748B', padding: '20px' }}>
                Loading categories...
              </div>
            ) : categoriesPosts.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748B', padding: '20px' }}>
                No posts available
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {categoriesPosts.map((category, idx) => {
                  const categoryColors = ['#DC2626', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];
                  const mainColor = categoryColors[idx % categoryColors.length];
                  return (
                    <div key={category.name} style={{
                      padding: '12px',
                      background: `${mainColor}08`,
                      borderRadius: '10px',
                      border: `1px solid ${mainColor}20`,
                      transition: 'all 0.2s ease',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontSize: '24px', fontWeight: '400', color: '#0F172A', maxWidth: '70%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Ramabhadra, serif' }}>
                          {category.name}
                        </span>
                        <span style={{ fontSize: '24px', fontWeight: '500', color: '#fff', background: mainColor, padding: '4px 16px', borderRadius: '8px', fontFamily: 'Ramabhadra, serif', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          {category.total}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {category.daysList.map((day, dayIdx) => (
                          <div key={dayIdx} style={{
                            flex: 1,
                            minWidth: '0',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '4px',
                          }}>
                            <div style={{
                              width: '100%',
                              height: '28px',
                              background: day.count > 0 ? mainColor : '#E2E8F0',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '16px',
                              fontWeight: '400',
                              color: day.count > 0 ? '#fff' : '#94A3B8',
                              transition: 'all 0.2s ease',
                              fontFamily: 'Ramabhadra, serif',
                            }}>
                              {day.count > 0 ? day.count : '-'}
                            </div>
                            <span style={{ fontSize: '15px', color: '#64748B', fontWeight: '400', fontFamily: 'Ramabhadra, serif' }}>
                              {day.date}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}
